import React from 'react';
import { Paper, Table, TableBody, TableCell, TableContainer, TableHead, TableRow, IconButton, Menu, MenuItem, ListItemIcon, ListItemText, Typography, Box, Tooltip, Backdrop, Divider, Button, Avatar, TableSortLabel } from '@mui/material';
import MoreVertIcon from '@mui/icons-material/MoreVert';
import { Close, DragIndicator, UnfoldMore } from '@mui/icons-material';
import { type OfferStandaloneReceivedOfferDTO } from '../../services/offerStandaloneManageService';
import EditIcon from '../icons/EditIcon';
import DeleteIcon from '@mui/icons-material/Delete';

interface Props {
  rows: OfferStandaloneReceivedOfferDTO[];
  onEdit?: (row: OfferStandaloneReceivedOfferDTO) => void;
  onDelete?: (row: OfferStandaloneReceivedOfferDTO) => void;
  onAddClick?: () => void;
}

const OfferReceivedOffersTable: React.FC<Props> = ({ rows, onEdit, onDelete, onAddClick }) => {
  const [anchorEl, setAnchorEl] = React.useState<null | HTMLElement>(null);
  const [menuRow, setMenuRow] = React.useState<OfferStandaloneReceivedOfferDTO | null>(null);

  // Tryb zmiany szerokości kolumn
  const [colResizeMode, setColResizeMode] = React.useState<boolean>(false);
  const [dragCol, setDragCol] = React.useState<{ key: string; startX: number; startW: number; rightKey?: string; startWRight?: number } | null>(null);
  const defaultColumnWidths = React.useMemo<Record<string, number>>(() => ({
    id: 180,
    kp: 56,
    package: 200,
    contractor: 260,
    type: 120,
    price: 160,
    status: 140,
    doc: 120,
    notes: 200,
    actions: 80,
  }), []);
  const STORAGE_KEY = React.useMemo(() => 'offerReceivedOffersColWidths', []);
  const [columnWidths, setColumnWidths] = React.useState<Record<string, number>>(defaultColumnWidths);
  const getWidthNum = React.useCallback((key: string) => columnWidths[key] ?? defaultColumnWidths[key] ?? 40, [columnWidths, defaultColumnWidths]);
  const getColW = React.useCallback((key: string) => `${getWidthNum(key)}px`, [getWidthNum]);

  // Konfiguracja widoczności i kolejności kolumn
  const ALL_COLUMNS = React.useMemo<string[]>(() => ['id','kp','package','contractor','type','price','status','doc','notes','actions'], []);
  const STORAGE_PREFIX = 'offerReceivedOffersTableCol';
  const VISIBLE_KEY = `${STORAGE_PREFIX}:visible`;
  const ORDER_KEY = `${STORAGE_PREFIX}:order`;
  const [columnOrder, setColumnOrder] = React.useState<string[]>(() => {
    try {
      const raw = localStorage.getItem(ORDER_KEY);
      if (raw) {
        const ord = JSON.parse(raw);
        if (Array.isArray(ord) && ord.length) {
          const filtered = ord.filter((k:string)=>ALL_COLUMNS.includes(k));
          // Zawsze upewnij się, że 'id' jest na początku, 'kp' zaraz po nim, a 'package' po 'kp'
          const idIndex = filtered.indexOf('id');
          const kpIndex = filtered.indexOf('kp');
          const packageIndex = filtered.indexOf('package');
          
          // Usuń 'id', 'kp' i 'package' z obecnych pozycji
          if (idIndex >= 0) filtered.splice(idIndex, 1);
          if (kpIndex >= 0 && kpIndex !== idIndex) filtered.splice(filtered.indexOf('kp'), 1);
          if (packageIndex >= 0 && packageIndex !== idIndex && packageIndex !== kpIndex) filtered.splice(filtered.indexOf('package'), 1);
          
          // Dodaj 'id' na początku, 'kp' zaraz po nim, a 'package' po 'kp'
          if (!filtered.includes('id')) filtered.unshift('id');
          if (!filtered.includes('kp')) filtered.splice(1, 0, 'kp');
          if (!filtered.includes('package')) filtered.splice(2, 0, 'package');
          
          // Jeśli 'id' nie jest pierwszy, popraw to
          if (filtered[0] !== 'id') {
            filtered.splice(filtered.indexOf('id'), 1);
            filtered.unshift('id');
          }
          // Jeśli 'kp' nie jest drugi, popraw to
          if (filtered.length > 1 && filtered[1] !== 'kp') {
            const kpPos = filtered.indexOf('kp');
            if (kpPos >= 0) {
              filtered.splice(kpPos, 1);
              filtered.splice(1, 0, 'kp');
            } else {
              filtered.splice(1, 0, 'kp');
            }
          }
          // Jeśli 'package' nie jest trzeci, popraw to
          if (filtered.length > 2 && filtered[2] !== 'package') {
            const packagePos = filtered.indexOf('package');
            if (packagePos >= 0) {
              filtered.splice(packagePos, 1);
              filtered.splice(2, 0, 'package');
            } else {
              filtered.splice(2, 0, 'package');
            }
          }
          
          // Dodaj pozostałe brakujące kolumny na końcu
          const missing = ALL_COLUMNS.filter(k => !filtered.includes(k));
          return filtered.concat(missing);
        }
      }
    } catch {}
    return [...ALL_COLUMNS];
  });
  
  // Upewnij się, że 'id' jest zawsze na początku, 'kp' zaraz po nim, a 'package' po 'kp' (korekta w czasie rzeczywistym)
  React.useEffect(() => {
    if (columnOrder.length > 0) {
      const corrected = [...columnOrder];
      const idIndex = corrected.indexOf('id');
      const kpIndex = corrected.indexOf('kp');
      const packageIndex = corrected.indexOf('package');
      
      // Usuń 'id', 'kp' i 'package' z obecnych pozycji
      if (idIndex >= 0) corrected.splice(idIndex, 1);
      if (kpIndex >= 0 && kpIndex !== idIndex) {
        const newKpIndex = corrected.indexOf('kp');
        if (newKpIndex >= 0) corrected.splice(newKpIndex, 1);
      }
      if (packageIndex >= 0 && packageIndex !== idIndex && packageIndex !== kpIndex) {
        const newPackageIndex = corrected.indexOf('package');
        if (newPackageIndex >= 0) corrected.splice(newPackageIndex, 1);
      }
      
      // Dodaj 'id' na początku, 'kp' zaraz po nim, a 'package' po 'kp'
      corrected.unshift('id');
      if (!corrected.includes('kp')) {
        corrected.splice(1, 0, 'kp');
      } else {
        const kpPos = corrected.indexOf('kp');
        if (kpPos !== 1) {
          corrected.splice(kpPos, 1);
          corrected.splice(1, 0, 'kp');
        }
      }
      if (!corrected.includes('package')) {
        corrected.splice(2, 0, 'package');
      } else {
        const packagePos = corrected.indexOf('package');
        if (packagePos !== 2) {
          corrected.splice(packagePos, 1);
          corrected.splice(2, 0, 'package');
        }
      }
      
      // Sprawdź czy coś się zmieniło
      if (corrected.length !== columnOrder.length || corrected[0] !== columnOrder[0] || (corrected.length > 1 && corrected[1] !== columnOrder[1]) || (corrected.length > 2 && corrected[2] !== columnOrder[2])) {
        setColumnOrder(corrected);
        try {
          localStorage.setItem(ORDER_KEY, JSON.stringify(corrected));
        } catch {}
      }
    }
  }, [columnOrder]);
  const [columnsVisible, setColumnsVisible] = React.useState<Record<string, boolean>>(() => {
    try {
      const raw = localStorage.getItem(VISIBLE_KEY);
      if (raw) {
        const obj = JSON.parse(raw);
        if (obj && typeof obj === 'object') return obj;
      }
    } catch {}
    return {};
  });
  const colV = React.useCallback((key: string) => columnsVisible[key] !== false, [columnsVisible]);

  // Panel „Dostosuj kolumny”
  const [colsOpen, setColsOpen] = React.useState<boolean>(false);
  const [draftColumnsVisible, setDraftColumnsVisible] = React.useState<Record<string, boolean> | null>(null);
  const [draftOrder, setDraftOrder] = React.useState<string[] | null>(null);
  const [draftWidths, setDraftWidths] = React.useState<Record<string, number>>({});
  const [colsPosition, setColsPosition] = React.useState<{ top:number; left:number }>({ top: 0, left: 0 });
  const [colsDragging, setColsDragging] = React.useState<{ startX:number; startY:number; top:number; left:number } | null>(null);
  const COLUMNS_DIALOG = { width: 305, height: 420 } as const;

  React.useEffect(() => {
    if (!colsDragging) return;
    const onMove = (e: MouseEvent) => {
      const dx = e.clientX - colsDragging.startX;
      const dy = e.clientY - colsDragging.startY;
      const nextTop = Math.max(0, Math.min((window?.innerHeight || 0) - COLUMNS_DIALOG.height, colsDragging.top + dy));
      const nextLeft = Math.max(0, Math.min((window?.innerWidth || 0), colsDragging.left + dx));
      setColsPosition({ top: nextTop, left: nextLeft });
    };
    const onUp = () => setColsDragging(null);
    window.addEventListener('mousemove', onMove);
    window.addEventListener('mouseup', onUp);
    return () => { window.removeEventListener('mousemove', onMove); window.removeEventListener('mouseup', onUp); };
  }, [colsDragging]);

  // Inicjalizacja szerokości z localStorage (tylko raz przy starcie)
  React.useEffect(() => {
    try {
      const raw = localStorage.getItem(STORAGE_KEY);
      if (raw) {
        const parsed = JSON.parse(raw);
        if (parsed && typeof parsed === 'object') {
          setColumnWidths((prev) => {
            // Połącz zapisane wartości z domyślnymi, aby upewnić się że wszystkie kolumny mają szerokość
            return { ...defaultColumnWidths, ...parsed };
          });
        }
      }
    } catch {}
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []); // Tylko raz przy montowaniu

  // Zapis ustawień do localStorage
  React.useEffect(() => { 
    try { localStorage.setItem(ORDER_KEY, JSON.stringify(columnOrder)); } catch {} 
  }, [ORDER_KEY, columnOrder]);
  
  React.useEffect(() => { 
    try { localStorage.setItem(VISIBLE_KEY, JSON.stringify(columnsVisible)); } catch {} 
  }, [VISIBLE_KEY, columnsVisible]);
  
  React.useEffect(() => { 
    try { localStorage.setItem(STORAGE_KEY, JSON.stringify(columnWidths)); } catch {} 
  }, [STORAGE_KEY, columnWidths]);

  React.useEffect(() => {
    const onMove = (e: MouseEvent) => {
      if (!dragCol) return;
      const dx = e.clientX - dragCol.startX;
      const minW = 24;
      const newW = Math.max(minW, dragCol.startW + dx);
      if (dragCol.rightKey && typeof dragCol.startWRight === 'number') {
        const newRight = Math.max(minW, (dragCol.startWRight as number) - dx);
        setColumnWidths((prev) => ({ ...prev, [dragCol.key]: newW, [dragCol.rightKey as string]: newRight }));
        setDraftWidths((prev) => ({ ...prev, [dragCol.key]: newW, [dragCol.rightKey as string]: newRight }));
      } else {
        setColumnWidths((prev) => ({ ...prev, [dragCol.key]: newW }));
        setDraftWidths((prev) => ({ ...prev, [dragCol.key]: newW }));
      }
    };
    const onUp = () => setDragCol(null);
    if (dragCol) {
      window.addEventListener('mousemove', onMove);
      window.addEventListener('mouseup', onUp, { once: true } as any);
    }
    return () => { window.removeEventListener('mousemove', onMove); };
  }, [dragCol]);

  // Obsługa ESC do zamykania okienka "Dostosuj kolumny"
  React.useEffect(() => {
    if (!colsOpen) return;
    const onKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape' || e.keyCode === 27) {
        setColsOpen(false);
        setColResizeMode(false);
        setDraftColumnsVisible(null);
        setDraftOrder(null);
        setDraftWidths({});
      }
    };
    window.addEventListener('keydown', onKeyDown);
    return () => { window.removeEventListener('keydown', onKeyDown); };
  }, [colsOpen]);

  const renderResizableTh = (keyId: string, node: React.ReactNode, align?: 'left'|'right'|'center', tooltipTitle?: string) => {
    const content = keyId === 'contractor' ? <Box sx={{ pl: '16px' }}>{node}</Box> : node;
    const wrappedContent = tooltipTitle ? (
      <Tooltip title={tooltipTitle} arrow>
        <span style={{ display: 'inline-flex', alignItems: 'center', justifyContent: align === 'center' ? 'center' : align === 'right' ? 'flex-end' : 'flex-start', width: '100%' }}>
          {content}
        </span>
      </Tooltip>
    ) : content;
    
    return (
    <TableCell align={align} sx={{ padding: '1px 4px', fontSize: '0.85rem', borderRadius: 0, width: getColW(keyId), maxWidth: 'none', position: 'relative', userSelect: 'none', backgroundColor: '#FAFAFA', borderRight: keyId === 'actions' ? 'none' : '1px solid #d4d4d9' }}>
      {wrappedContent}
      {colResizeMode ? (
        <Box
          onMouseDown={(e) => {
            e.preventDefault(); e.stopPropagation();
            const visOrder = (draftOrder ?? columnOrder).filter((k)=> colV(k));
            const idx = visOrder.indexOf(keyId);
            const rightKey = idx >= 0 && idx < visOrder.length - 1 ? visOrder[idx+1] : undefined;
            setDragCol({
              key: keyId,
              startX: e.clientX,
              startW: getWidthNum(keyId),
              rightKey,
              startWRight: rightKey ? getWidthNum(rightKey) : undefined
            });
          }}
          sx={{ position: 'absolute', right: 0, top: 0, width: '12px', height: '100%', cursor: 'col-resize', zIndex: 6, touchAction: 'none',
            backgroundImage: 'linear-gradient(to right, transparent 45%, #FF0000 45%, #FF0000 55%, transparent 55%)',
            '&:hover': { backgroundColor: 'rgba(0,0,0,0.06)' }           }}
        />
      ) : null}
    </TableCell>
    );
  };

  const openMenu = (event: React.MouseEvent<HTMLElement>, row: OfferStandaloneReceivedOfferDTO) => {
    event.stopPropagation();
    setMenuRow(row);
    setAnchorEl(event.currentTarget);
  };

  const closeMenu = () => {
    setAnchorEl(null);
    setMenuRow(null);
  };

  // Sortowanie
  type SortKey = 'id' | 'kp' | 'package' | 'contractor' | 'type' | 'price' | 'status' | 'doc' | 'notes';
  const [orderBy, setOrderBy] = React.useState<SortKey>('id');
  const [order, setOrder] = React.useState<'asc' | 'desc'>('asc');
  
  const handleRequestSort = (property: SortKey) => {
    if (orderBy === property) {
      setOrder((prev) => (prev === 'asc' ? 'desc' : 'asc'));
    } else {
      setOrderBy(property);
      setOrder('asc');
    }
  };

  // Funkcja do wyodrębnienia liczby porządkowej z received_offer_id (format: RO.[liczba].OF...)
  const extractSequenceNumber = (receivedOfferId: string): number => {
    if (!receivedOfferId) return 0;
    const match = receivedOfferId.match(/^RO\.(\d+)\./);
    return match ? parseInt(match[1], 10) : 0;
  };

  const sortedRows = React.useMemo(() => {
    const arr = [...rows];
    const dir = order === 'asc' ? 1 : -1;
    arr.sort((a, b) => {
      switch (orderBy) {
        case 'id': {
          // Sortowanie numeryczne po liczbie porządkowej z received_offer_id
          const aId = a.received_offer_id || a.offer_id || '';
          const bId = b.received_offer_id || b.offer_id || '';
          const aSeq = extractSequenceNumber(aId);
          const bSeq = extractSequenceNumber(bId);
          if (aSeq !== bSeq) return (aSeq - bSeq) * dir;
          // Jeśli liczby są równe, sortuj alfabetycznie po całym ID
          return String(aId).localeCompare(String(bId)) * dir;
        }
        case 'kp': {
          const av = String((a as any).created_by_name || '');
          const bv = String((b as any).created_by_name || '');
          return av.localeCompare(bv) * dir;
        }
        case 'package': {
          const av = String((a as any).package_name || '');
          const bv = String((b as any).package_name || '');
          return av.localeCompare(bv) * dir;
        }
        case 'contractor': {
          const av = String(a.contractor_name || '');
          const bv = String(b.contractor_name || '');
          return av.localeCompare(bv) * dir;
        }
        case 'type': {
          const av = String(a.offer_type || '');
          const bv = String(b.offer_type || '');
          return av.localeCompare(bv) * dir;
        }
        case 'price': {
          const av = Number(a.offered_price || 0);
          const bv = Number(b.offered_price || 0);
          return (av - bv) * dir;
        }
        case 'status': {
          const av = String(a.status || '');
          const bv = String(b.status || '');
          return av.localeCompare(bv) * dir;
        }
        case 'doc': {
          const av = String(a.documentation_link || '');
          const bv = String(b.documentation_link || '');
          return av.localeCompare(bv) * dir;
        }
        case 'notes': {
          const av = String(a.notes || '');
          const bv = String(b.notes || '');
          return av.localeCompare(bv) * dir;
        }
        default:
          return 0;
      }
    });
    return arr;
  }, [rows, orderBy, order]);

  const StrongSortIcon = () => (<UnfoldMore sx={{ width: 12, height: 12, color: '#2D2D32' }} />);
  const MutedSortIcon = () => (<UnfoldMore sx={{ width: 12, height: 12, color: '#d4d4d9' }} />);
  
  const getSortLabelProps = (keyId: SortKey) => {
    const isActive = orderBy === keyId;
    return {
      IconComponent: (isActive ? StrongSortIcon : MutedSortIcon) as any,
      active: isActive,
      direction: isActive ? order : 'asc' as 'asc' | 'desc',
      hideSortIcon: false,
      onClick: () => handleRequestSort(keyId),
      sx: {
        '& .MuiTableSortLabel-icon': {
          color: isActive ? '#2D2D32' : '#d4d4d9'
        }
      }
    };
  };

  return (
    <Box sx={{ position: 'relative', width: '100%' }}>
      {/* Pasek akcji – nad tabelą, wyrównany do prawej */}
      <Box sx={{ position: 'absolute', top: -36, right: 0, display: 'flex', alignItems: 'center', zIndex: 3 }}>
        {/* Grupa akcji */}
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          {onAddClick && (
            <Tooltip title="Dodaj ofertę">
              <span>
                <IconButton onClick={onAddClick} data-hotkey-add="true">
                  <Box component="img" src="/assets/icons/add.svg" alt="Dodaj" sx={{ width: 22, height: 22 }} />
                </IconButton>
              </span>
            </Tooltip>
          )}
        </Box>
        {/* Odstęp 16px między grupami ikon */}
        <Box sx={{ width: 16 }} />
        {/* Dostosuj kolumny – ostatnia ikona, na prawej krawędzi */}
        <Box sx={{ display: 'flex', alignItems: 'center' }}>
          <IconButton
            size="small"
            sx={{
              width: 32,
              height: 32,
              borderRadius: '4px',
              p: 0,
              alignSelf: 'flex-end',
              bgcolor: colResizeMode ? '#E0E0F5' : 'transparent',
              transition: 'background-color 0.15s ease',
              '&:hover': { bgcolor: 'rgba(0,0,0,0.06)' }
            }}
            onClick={() => { 
              setDraftColumnsVisible({ ...columnsVisible });
              setDraftOrder([...columnOrder]);
              setDraftWidths({ ...columnWidths });
              setColResizeMode(true); 
              setColsOpen(true); 
              setColsPosition({ top: 0, left: 0 }); 
            }}
          >
            <Box
              component="img"
              alt="kolumny"
              src={'/assets/icons/frame-344.svg?v=2'}
              sx={{ width: 32, height: 32 }}
            />
          </IconButton>
        </Box>
      </Box>

    <TableContainer 
      component={Paper} 
      sx={{ 
        width: '100%',
        mt: 0,
        height: '455px',
        overflow: 'auto',
        position: 'relative',
        border: '1px solid #d0d0d6',
        borderBottom: '1px solid #d0d0d6',
        borderRadius: 0,
        borderTopRightRadius: 4,
        borderBottomLeftRadius: 4,
        borderBottomRightRadius: 4,
        overflowX: 'hidden',
        overflowY: 'auto',
        boxShadow: 'none',
        scrollbarWidth: 'none',
        msOverflowStyle: 'none',
        '&::-webkit-scrollbar': { width: 0, height: 0 }
      }}
    >
      <Table 
        size="small"
        stickyHeader
        sx={{
          tableLayout: 'fixed',
          width: '100%',
          '& .MuiTableCell-root': { padding: '1px 4px', fontSize: '0.85rem', borderRadius: 0, fontFamily: 'Roboto, sans-serif' },
          '& .MuiTableCell-head': { position: 'sticky', top: 0, zIndex: 2, backgroundColor: '#FAFAFA', borderRight: '1px solid #d4d4d9', borderRadius: 0, boxShadow: 'none' },
          '& .MuiTableCell-body': { borderRight: 'none', borderRadius: 0, color: '#2D2D32', fontFamily: 'Roboto, sans-serif' },
          '& .MuiTableRow-head > *:last-child': { borderRight: 'none', borderTopRightRadius: 4 }
        }}
      >
        {(() => {
          const ord = (draftOrder ?? columnOrder);
          return (
            <colgroup>
              {ord.filter((k)=>k!=='actions').map((key) => (
                colV(key) ? <col key={`col-${key}`} style={{ width: getColW(key) }} /> : null
              ))}
              {colV('actions') ? <col key="col-actions" style={{ width: getColW('actions') }} /> : null}
            </colgroup>
          );
        })()}
        <TableHead>
          <TableRow>
            {(draftOrder ?? columnOrder).filter((k)=>k !== 'actions').map((key) => colV(key) ? renderResizableTh(key, (
              key === 'id' ? (
                <TableSortLabel {...getSortLabelProps('id')}>
                  ID
                </TableSortLabel>
              ) :               key === 'kp' ? (
                <Tooltip title="Utworzył/a" arrow>
                  <span>
                    <TableSortLabel {...getSortLabelProps('kp')}>
                      U
                    </TableSortLabel>
                  </span>
                </Tooltip>
              ) : key === 'package' ? (
                <TableSortLabel {...getSortLabelProps('package')}>
                  Nazwa pakietu
                </TableSortLabel>
              ) : key === 'contractor' ? (
                <TableSortLabel {...getSortLabelProps('contractor')}>
                  Wykonawca/Dostawca
                </TableSortLabel>
              ) : key === 'type' ? (
                <TableSortLabel {...getSortLabelProps('type')}>
                  Typ
                </TableSortLabel>
              ) : key === 'price' ? (
                <TableSortLabel {...getSortLabelProps('price')}>
                  Cena ofertowa
                </TableSortLabel>
              ) : key === 'status' ? (
                <TableSortLabel {...getSortLabelProps('status')}>
                  Status
                </TableSortLabel>
              ) : key === 'doc' ? (
                <TableSortLabel {...getSortLabelProps('doc')}>
                  Dokument
                </TableSortLabel>
              ) : key === 'notes' ? (
                <TableSortLabel {...getSortLabelProps('notes')}>
                  Uwagi
                </TableSortLabel>
              ) : ''
            ), key === 'kp' ? 'center' : key === 'price' ? 'right' : undefined) : null)}
            {colV('actions') ? renderResizableTh('actions', '', 'center') : null}
          </TableRow>
        </TableHead>
        <TableBody>
          {rows.length === 0 ? (
            <TableRow>
              <TableCell colSpan={(draftOrder ?? columnOrder).filter((k)=>colV(k)).length} align="center">
                <Typography variant="body2" sx={{ py: 2, fontFamily: 'Roboto, sans-serif', color: '#2D2D32' }}>
                  Brak ofert
                </Typography>
              </TableCell>
            </TableRow>
          ) : (
            sortedRows.map((o) => {
              const ord = (draftOrder ?? columnOrder).filter((k)=>k !== 'actions');
              const offerAny = o as any;
              return (
                <TableRow 
                  key={o.offer_id} 
                  hover
                  sx={{ 
                    '&:hover': { backgroundColor: '#F5F5FF !important' },
                    '&.MuiTableRow-hover:hover': { backgroundColor: '#F5F5FF !important' }
                  }}
                >
                  {ord.map((key) => {
                    if (!colV(key)) return null;
                    if (key === 'kp') {
                      return (
                        <TableCell key={key} align="center" sx={{ width: getColW('kp') }}>
                          {(() => {
                            const avatarUrl = offerAny.created_by_avatar_url;
                            const userName = offerAny.created_by_name;
                            
                            // Sprawdź czy mamy dane użytkownika (nie null, nie undefined, nie pusty string)
                            const hasAvatarUrl = avatarUrl && typeof avatarUrl === 'string' && avatarUrl.trim().length > 0;
                            const hasUserName = userName && typeof userName === 'string' && userName.trim().length > 0;
                            const hasUser = hasAvatarUrl || hasUserName;
                            
                            if (hasUser) {
                              return (
                                <Tooltip title={userName || ''} arrow>
                                  <Avatar src={hasAvatarUrl ? avatarUrl : undefined} sx={{ width: 25, height: 25, fontSize: 12 }}>
                                    {hasUserName ? userName.substring(0, 1).toUpperCase() : '?'}
                                  </Avatar>
                                </Tooltip>
                              );
                            } else {
                              return (
                                <Tooltip title="AI" arrow>
                                  <Avatar sx={{ width: 25, height: 25, fontSize: 12, bgcolor: 'transparent', p: 0 }}>
                                    <Box component="img" src="/assets/ai_badge_unitresco.svg" alt="AI" sx={{ width: 25, height: 25 }} />
                                  </Avatar>
                                </Tooltip>
                              );
                            }
                          })()}
                        </TableCell>
                      );
                    }
                    if (key === 'id') {
                      return (
                        <TableCell key={key} sx={{ width: getColW('id') }}>
                          <Box sx={{ pl: '16px' }}>{o.received_offer_id || o.offer_id}</Box>
                        </TableCell>
                      );
                    }
                    if (key === 'package') {
                      return (
                        <TableCell key={key} sx={{ width: getColW('package') }}>
                          <Box sx={{ pl: '16px' }}>{(o as any).package_name || '-'}</Box>
                        </TableCell>
                      );
                    }
                    if (key === 'contractor') {
                      return (
                        <TableCell key={key} sx={{ width: getColW('contractor') }}>
                          <Box sx={{ pl: '16px' }}>{o.contractor_name}</Box>
                        </TableCell>
                      );
                    }
                    if (key === 'type') {
                      return (
                        <TableCell key={key} sx={{ width: getColW('type') }}>{o.offer_type || '-'}</TableCell>
                      );
                    }
                    if (key === 'price') {
                      return (
                        <TableCell key={key} align="right" sx={{ width: getColW('price') }}>
                          {o.offered_price !== null && o.offered_price !== undefined
                            ? new Intl.NumberFormat('pl-PL', { style: 'decimal', maximumFractionDigits: 0 }).format(o.offered_price)
                            : '-'}
                        </TableCell>
                      );
                    }
                    if (key === 'status') {
                      return (
                        <TableCell key={key} sx={{ width: getColW('status') }}>{o.status || '-'}</TableCell>
                      );
                    }
                    if (key === 'doc') {
                      return (
                        <TableCell key={key} sx={{ width: getColW('doc') }}>
                          {o.documentation_link ? <a href={o.documentation_link} target="_blank" rel="noreferrer" style={{ color: '#1976D2', textDecoration: 'none' }}>Otwórz</a> : '-'}
                        </TableCell>
                      );
                    }
                    if (key === 'notes') {
                      return (
                        <TableCell key={key} sx={{ width: getColW('notes') }}>
                          <Box sx={{ pl: '16px', whiteSpace: 'pre-wrap', wordBreak: 'break-word' }}>{o.notes || '-'}</Box>
                        </TableCell>
                      );
                    }
                    return null;
                  })}
                  {colV('actions') ? (
                    <TableCell align="center" sx={{ width: getColW('actions') }}>
                      <IconButton size="small" onClick={(e) => openMenu(e, o)} aria-label="więcej">
                        <MoreVertIcon fontSize="small" sx={{ color: '#2D2D32' }} />
                      </IconButton>
                    </TableCell>
                  ) : null}
                </TableRow>
              );
            })
          )}
        </TableBody>
      </Table>

      <Menu anchorEl={anchorEl} open={Boolean(anchorEl)} onClose={closeMenu}>
        <MenuItem onClick={() => { if (menuRow && onEdit) onEdit(menuRow); closeMenu(); }}>
          <ListItemIcon sx={{ minWidth: 28 }}>
            <Box component="img" src="/assets/icons/edit2.svg" alt="Edytuj" sx={{ width: 18, height: 18 }} />
          </ListItemIcon>
          <ListItemText primary="Edytuj" primaryTypographyProps={{ fontFamily: 'Roboto, sans-serif', fontWeight: 400, fontSize: 14, color: '#2D2D32' }} />
        </MenuItem>
      </Menu>
    </TableContainer>

      {/* Okno „Dostosuj kolumny” – spójne z tabelą projektów */}
      <Backdrop open={colsOpen} sx={{ color: '#fff', zIndex: (theme) => theme.zIndex.modal + 1, backgroundColor: 'rgba(0,0,0,0.35)', pointerEvents: 'none' }} onClick={()=> { 
        setColsOpen(false);
        setDraftColumnsVisible(null);
        setDraftOrder(null);
        setDraftWidths({});
      }}>
        <Paper
          elevation={8}
          sx={{
            position: 'fixed',
            bottom: 0,
            right: 0,
            height: `${COLUMNS_DIALOG.height}px`,
            width: `${COLUMNS_DIALOG.width}px`,
            display: 'flex',
            flexDirection: 'column',
            boxShadow: '0px 5px 30px rgba(0,0,0,0.15)',
            borderRadius: 0,
            zIndex: (theme) => theme.zIndex.modal + 2,
            backgroundColor: '#fff',
            top: colsPosition.top ? `${colsPosition.top}px` : undefined,
            left: colsPosition.left ? `${colsPosition.left}px` : undefined,
            pointerEvents: 'auto'
          }}
          onClick={(e)=> e.stopPropagation()}
        >
          <Box
            sx={{ px: 2, py: 1, display: 'flex', alignItems: 'center', justifyContent: 'space-between', flex: '0 0 auto', position: 'sticky', top: 0, bgcolor: '#FFFFFF', zIndex: 1, cursor: 'move', userSelect: 'none' }}
            onMouseDown={(e)=> setColsDragging({ startX: e.clientX, startY: e.clientY, top: colsPosition.top, left: colsPosition.left })}
          >
            <Typography sx={{ fontFamily: 'Roboto, sans-serif', fontWeight: 400, fontSize: 16, color: '#2D2D32' }}>Dostosuj kolumny</Typography>
            <IconButton size="small" onClick={()=>{ 
              setColsOpen(false); 
              setColResizeMode(false); 
              setDraftColumnsVisible(null);
              setDraftOrder(null);
              setDraftWidths({});
            }}>
              <Close fontSize="small" />
            </IconButton>
          </Box>
          <Divider />
          <Box sx={{ px: 0, py: 1.5, flex: '1 1 auto', minHeight: 0, overflowY: 'auto' }}>
            {(() => {
              const list = draftColumnsVisible ?? columnsVisible;
              if (draftColumnsVisible == null) setDraftColumnsVisible({ ...columnsVisible });
              const ord = draftOrder ?? columnOrder;
              if (draftOrder == null) setDraftOrder([...columnOrder]);
              return ord.map((key) => (
                <Box key={key} sx={{ py: 1, px: 2 }}
                  draggable
                  onDragStart={(e)=>{ e.dataTransfer.setData('text/plain', key); }}
                  onDragOver={(e)=>{ e.preventDefault(); }}
                  onDrop={(e)=>{ e.preventDefault(); const src = e.dataTransfer.getData('text/plain'); if (!src || src===key) return; setDraftOrder(prev=>{ const cur = [...(prev||ord)]; const from = cur.indexOf(src); const to = cur.indexOf(key); if (from<0||to<0) return cur; cur.splice(to,0,cur.splice(from,1)[0]); return cur; }); }}
                >
                  <Box sx={{ border: '1px solid #d4d4d9', borderRadius: '4px', height: 36, width: 257, display: 'flex', alignItems: 'center', justifyContent: 'space-between', px: 1.25 }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.25 }}>
                      <IconButton onClick={()=>setDraftColumnsVisible(prev=>({ ...(prev||{}), [key]: !((prev||list)[key] !== false) }))} size="small" sx={{ p: 0 }} aria-label="toggle column">
                        <Box component="img" src={(draftColumnsVisible ?? columnsVisible)[key] !== false ? '/assets/icons/checkbox-on.svg' : '/assets/icons/checkbox-off.svg'} alt="check" sx={{ width: 23, height: 24 }} />
                      </IconButton>
                      <Typography sx={{ fontFamily: 'Roboto, sans-serif', fontSize: 14, color: '#2D2D32' }}>{(key === 'id' ? 'ID' : key === 'kp' ? 'U' : key === 'package' ? 'Nazwa pakietu' : key === 'contractor' ? 'Wykonawca/Dostawca' : key === 'type' ? 'Typ' : key === 'price' ? 'Cena ofertowa' : key === 'status' ? 'Status' : key === 'doc' ? 'Dokument' : key === 'notes' ? 'Uwagi' : key.toUpperCase())}</Typography>
                    </Box>
                    <DragIndicator fontSize="small" sx={{ color: '#595966', cursor: 'grab' }} />
                  </Box>
                </Box>
              ));
            })()}
          </Box>
          <Divider />
          <Box sx={{ px: 2, py: 1, display: 'flex', alignItems: 'center', justifyContent: 'flex-end', gap: '24px', flex: '0 0 auto', boxShadow: '0px 4px 4px rgba(0,0,0,0.05)', position: 'sticky', bottom: 0, bgcolor: '#FFFFFF', zIndex: 1 }}>
            <Button variant="text" onClick={()=>{ 
              setColsOpen(false); 
              setColResizeMode(false); 
              setDraftColumnsVisible(null);
              setDraftOrder(null);
              setDraftWidths({});
            }} sx={{ fontFamily: 'Roboto, sans-serif', fontWeight: 500, fontSize: 14, color: '#2D2D32', textTransform: 'none' }}>Anuluj</Button>
            <Button variant="contained" sx={{ width: 123, height: 40, borderRadius: '4px', textTransform: 'none', fontFamily: 'Roboto, sans-serif', fontWeight: 600, fontSize: 14, backgroundColor: '#020242', '&:hover': { backgroundColor: '#020242' } }} onClick={()=>{ 
              if (draftColumnsVisible) { 
                setColumnsVisible(draftColumnsVisible); 
                try { localStorage.setItem(VISIBLE_KEY, JSON.stringify(draftColumnsVisible)); } catch {} 
              } 
              if (draftOrder) { 
                const normalized = draftOrder.filter((k)=>ALL_COLUMNS.includes(k)).concat(ALL_COLUMNS.filter(k=>!draftOrder.includes(k))); 
                setColumnOrder(normalized); 
                try { localStorage.setItem(ORDER_KEY, JSON.stringify(normalized)); } catch {} 
              }
              // Zapisz szerokości kolumn - użyj aktualnych szerokości (mogły być zmienione przez przeciąganie)
              // Jeśli są zmiany w draftWidths, użyj ich, w przeciwnym razie użyj aktualnych columnWidths
              const widthsToSave = { ...columnWidths };
              const finalOrder = draftOrder ?? columnOrder;
              finalOrder.forEach((key) => {
                const draftW = draftWidths[key];
                if (draftW && typeof draftW === 'number' && draftW >= 24) {
                  widthsToSave[key] = draftW;
                }
              });
              // Zapisz wszystkie aktualne szerokości
              setColumnWidths(widthsToSave);
              try {
                localStorage.setItem(STORAGE_KEY, JSON.stringify(widthsToSave));
              } catch {}
              setColsOpen(false); 
              setColResizeMode(false);
              setDraftColumnsVisible(null);
              setDraftOrder(null);
              setDraftWidths({});
            }}>
              Zapisz zmiany
            </Button>
          </Box>
        </Paper>
      </Backdrop>
    </Box>
  );
};

export default OfferReceivedOffersTable;
